import {useMemo, type ReactNode} from "react";
import {
	PasswordStrengthValidationConfig,
	type PasswordStrengthValidation,
} from "@/features/auth/passwordStrengthValidation";
import type {PasswordRequirement} from "@/primitives/PasswordRequirements";

export interface UsePasswordRequirementsOptions {
	/**
	 * Localised text per rule name (`minimumLength`, `lowercase`, `uppercase`,
	 * `special`, `digit`). A rule with no label falls back to its raw name.
	 */
	labels?: Partial<Record<string, ReactNode>>;
}

/**
 * React adapter over the tested `PasswordStrengthValidationConfig.validate`. Returns
 * one `PasswordRequirement` per *required* rule with its live met/unmet state, ready
 * to hand to `PasswordRequirements`. Rules that aren't required are omitted.
 */
export function usePasswordRequirements(
	password: string,
	validation: PasswordStrengthValidation | undefined,
	options: UsePasswordRequirementsOptions = {},
): PasswordRequirement[] {
	const {labels} = options;
	return useMemo(() => {
		if (!validation) {
			return [];
		}
		const config = new PasswordStrengthValidationConfig(validation);
		const {errors = []} = config.validate(password);

		return PasswordStrengthValidationConfig.getRuleNames()
			.filter((name) => validation[name]?.required === true)
			.map((name) => ({
				id: name,
				label: labels?.[name] ?? name,
				met: !errors.includes(name),
			}));
	}, [password, validation, labels]);
}
